import {
  ActionIcon,
  Accordion,
  Group,
  Loader,
  Menu,
  Paper,
  Stack,
  Text,
  Badge,
  SimpleGrid,
  ThemeIcon,
} from '@mantine/core';
import { IconDots, IconEdit, IconTrash, IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { PhysicalExaminationData } from '@/components/inputs/physical-examination/types';
import { LabeledField } from '@/components/LabeledField';

export type PhysicalExaminationAction = 'edit' | 'remove';

export interface PhysicalExaminationListItemProps {
  data: PhysicalExaminationData;
  onAction: (action: PhysicalExaminationAction) => void;
  isDeleting?: boolean;
}

// Helper to check if section has text content (excluding normal field)
const sectionHasTextContent = (
  section: Record<string, string | boolean | undefined>
): boolean => {
  return Object.entries(section).some(
    ([key, value]) =>
      key !== 'normal' && typeof value === 'string' && value.trim() !== ''
  );
};


export const PhysicalExaminationListItem = ({
  data,
  onAction,
  isDeleting = false,
}: PhysicalExaminationListItemProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Check if vital signs have data
  const hasVitalSigns =
    data.vitalSigns.bloodPressureSystolic ||
    data.vitalSigns.bloodPressureDiastolic ||
    data.vitalSigns.heartRate ||
    data.vitalSigns.respiratoryRate ||
    data.vitalSigns.temperature ||
    data.vitalSigns.oxygenSaturation;

  // Check if each exam section has data OR is marked as normal
  const headNeckIsNormal = data.headNeck.normal === true;
  const hasHeadNeckContent = sectionHasTextContent(data.headNeck);
  const hasHeadNeck = headNeckIsNormal || hasHeadNeckContent;

  const thoraxIsNormal = data.thorax.normal === true;
  const hasThoraxContent = sectionHasTextContent(data.thorax);
  const hasThorax = thoraxIsNormal || hasThoraxContent;

  const abdomenIsNormal = data.abdomen.normal === true;
  const hasAbdomenContent = sectionHasTextContent(data.abdomen);
  const hasAbdomen = abdomenIsNormal || hasAbdomenContent;

  const extremitiesIsNormal = data.extremities.normal === true;
  const hasExtremitiesContent = sectionHasTextContent(data.extremities);
  const hasExtremities = extremitiesIsNormal || hasExtremitiesContent;

  const neurologicalIsNormal = data.neurological.normal === true;
  const hasNeurologicalContent = sectionHasTextContent(data.neurological);
  const hasNeurological = neurologicalIsNormal || hasNeurologicalContent;

  // Count filled sections
  const filledSections = [
    hasVitalSigns,
    hasHeadNeck,
    hasThorax,
    hasAbdomen,
    hasExtremities,
    hasNeurological,
  ].filter(Boolean).length;

  const canEdit = !isDeleting;
  const canRemove = !isDeleting;
  const hasActions = canEdit || canRemove;

  // Build default expanded sections (only sections with actual text content)
  const defaultExpandedSections = [
    hasVitalSigns && 'vitalSigns',
    hasHeadNeckContent && 'headNeck',
    hasThoraxContent && 'thorax',
    hasAbdomenContent && 'abdomen',
    hasExtremitiesContent && 'extremities',
    hasNeurologicalContent && 'neurological',
  ].filter(Boolean) as string[];

  // WNL-only sections (marked normal but no text content)
  const headNeckWnlOnly = headNeckIsNormal && !hasHeadNeckContent;
  const thoraxWnlOnly = thoraxIsNormal && !hasThoraxContent;
  const abdomenWnlOnly = abdomenIsNormal && !hasAbdomenContent;
  const extremitiesWnlOnly = extremitiesIsNormal && !hasExtremitiesContent;
  const neurologicalWnlOnly = neurologicalIsNormal && !hasNeurologicalContent;

  // Helper to render section title with normal indicator
  const renderSectionTitle = (
    title: string,
    isNormal: boolean,
    hasTextContent: boolean
  ) => (
    <Group gap="xs" justify="space-between" pr="xs">
      <Text size="sm" fw={500}>
        {title}
      </Text>
      {isNormal && !hasTextContent && (
        <Group gap="xs">
          <ThemeIcon size="xs" color="green" variant="light" radius="xl">
            <IconCheck size={10} />
          </ThemeIcon>
          <Text size="sm" c="green">
            {t('examination.physicalExamination.withinNormalLimits', {
              ns: 'patient-pages',
              defaultValue: 'No pathological findings',
            })}
          </Text>
        </Group>
      )}
    </Group>
  );

  // Render a WNL-only section as a non-expandable row
  const renderWnlOnlySection = (title: string) => (
    <Paper p="sm" withBorder radius="md" bg="var(--mantine-color-gray-0)">
      <Group gap="xs" justify="space-between">
        <Text size="sm" fw={500}>
          {title}
        </Text>
        <Group gap="xs">
          <ThemeIcon size="xs" color="green" variant="light" radius="xl">
            <IconCheck size={10} />
          </ThemeIcon>
          <Text size="sm" c="green">
            {t('examination.physicalExamination.withinNormalLimits', {
              ns: 'patient-pages',
              defaultValue: 'No pathological findings',
            })}
          </Text>
        </Group>
      </Group>
    </Paper>
  );

  return (
    <Paper p="sm" withBorder radius="md">
      <Stack gap="xs">
      {/* Header with badge and actions */}
      <Group justify="space-between">
        <Badge size="sm" variant="light" color="blue">
          {filledSections}{' '}
          {t('examination.physicalExamination.sectionsCompleted', {
            ns: 'patient-pages',
            defaultValue: 'sections',
          })}
        </Badge>

        {/* Actions menu or loading indicator */}
        {isDeleting ? (
          <Loader size="sm" />
        ) : (
          hasActions && (
            <Menu shadow="md" width={160} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="sm">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                {canEdit && (
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={() => onAction('edit')}
                  >
                    {t('edit', {
                      ns: 'common',
                      defaultValue: 'Edit',
                    })}
                  </Menu.Item>
                )}
                {canRemove && (
                  <Menu.Item
                    leftSection={<IconTrash size={14} />}
                    onClick={() => onAction('remove')}
                    color="red"
                  >
                    {t('delete', {
                      ns: 'common',
                      defaultValue: 'Delete',
                    })}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )
        )}
      </Group>

      {/* Accordion with all sections that have data */}
      <Accordion
        multiple
        defaultValue={defaultExpandedSections}
        variant="separated"
        radius="md"
      >
        {/* Vital Signs Section */}
        {hasVitalSigns && (
          <Accordion.Item value="vitalSigns">
            <Accordion.Control>
              <Text size="sm" fw={500}>
                {t('examination.physicalExamination.vitalSigns.title', {
                  ns: 'patient-pages',
                  defaultValue: 'Vital Signs',
                })}
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <SimpleGrid cols={3} spacing="xs">
                {(data.vitalSigns.bloodPressureSystolic ||
                  data.vitalSigns.bloodPressureDiastolic) && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.vitalSigns.bloodPressure',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'BP',
                      }
                    )}
                    value={`${data.vitalSigns.bloodPressureSystolic || '-'}/${data.vitalSigns.bloodPressureDiastolic || '-'} mmHg`}
                  />
                )}
                {data.vitalSigns.heartRate && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.vitalSigns.heartRateShort',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'HR',
                      }
                    )}
                    value={`${data.vitalSigns.heartRate} /min`}
                  />
                )}
                {data.vitalSigns.respiratoryRate && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.vitalSigns.respiratoryRateShort',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'RR',
                      }
                    )}
                    value={`${data.vitalSigns.respiratoryRate} /min`}
                  />
                )}
                {data.vitalSigns.temperature && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.vitalSigns.temperatureShort',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Temp',
                      }
                    )}
                    value={`${data.vitalSigns.temperature} °C`}
                  />
                )}
                {data.vitalSigns.oxygenSaturation && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.vitalSigns.oxygenSaturationShort',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'SpO2',
                      }
                    )}
                    value={`${data.vitalSigns.oxygenSaturation}%`}
                  />
                )}
              </SimpleGrid>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* Head & Neck Section */}
        {hasHeadNeckContent && (
          <Accordion.Item value="headNeck">
            <Accordion.Control>
              {renderSectionTitle(
                t('examination.physicalExamination.headNeck.title', {
                  ns: 'patient-pages',
                  defaultValue: 'Head & Neck',
                }),
                headNeckIsNormal,
                hasHeadNeckContent
              )}
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {data.headNeck.cranium && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.headNeck.cranium',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Cranium',
                      }
                    )}
                    value={data.headNeck.cranium}
                  />
                )}
                {data.headNeck.eyes && (
                  <LabeledField
                    label={t('examination.physicalExamination.headNeck.eyes', {
                      ns: 'patient-pages',
                      defaultValue: 'Eyes',
                    })}
                    value={data.headNeck.eyes}
                  />
                )}
                {data.headNeck.ent && (
                  <LabeledField
                    label={t('examination.physicalExamination.headNeck.ent', {
                      ns: 'patient-pages',
                      defaultValue: 'ENT',
                    })}
                    value={data.headNeck.ent}
                  />
                )}
                {data.headNeck.neck && (
                  <LabeledField
                    label={t('examination.physicalExamination.headNeck.neck', {
                      ns: 'patient-pages',
                      defaultValue: 'Neck',
                    })}
                    value={data.headNeck.neck}
                  />
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* Thorax Section */}
        {hasThoraxContent && (
          <Accordion.Item value="thorax">
            <Accordion.Control>
              {renderSectionTitle(
                t('examination.physicalExamination.thorax.title', {
                  ns: 'patient-pages',
                  defaultValue: 'Thorax',
                }),
                thoraxIsNormal,
                hasThoraxContent
              )}
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {data.thorax.respiratory && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.thorax.respiratory',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Respiratory',
                      }
                    )}
                    value={data.thorax.respiratory}
                  />
                )}
                {data.thorax.cardiovascular && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.thorax.cardiovascular',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Cardiovascular',
                      }
                    )}
                    value={data.thorax.cardiovascular}
                  />
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* Abdomen Section */}
        {hasAbdomenContent && (
          <Accordion.Item value="abdomen">
            <Accordion.Control>
              {renderSectionTitle(
                t('examination.physicalExamination.abdomen.title', {
                  ns: 'patient-pages',
                  defaultValue: 'Abdomen',
                }),
                abdomenIsNormal,
                hasAbdomenContent
              )}
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {data.abdomen.inspection && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.abdomen.inspection',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Inspection',
                      }
                    )}
                    value={data.abdomen.inspection}
                  />
                )}
                {data.abdomen.auscultation && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.abdomen.auscultation',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Auscultation',
                      }
                    )}
                    value={data.abdomen.auscultation}
                  />
                )}
                {data.abdomen.palpation && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.abdomen.palpation',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Palpation',
                      }
                    )}
                    value={data.abdomen.palpation}
                  />
                )}
                {data.abdomen.percussion && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.abdomen.percussion',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Percussion',
                      }
                    )}
                    value={data.abdomen.percussion}
                  />
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* Extremities Section */}
        {hasExtremitiesContent && (
          <Accordion.Item value="extremities">
            <Accordion.Control>
              {renderSectionTitle(
                t('examination.physicalExamination.extremities.title', {
                  ns: 'patient-pages',
                  defaultValue: 'Extremities',
                }),
                extremitiesIsNormal,
                hasExtremitiesContent
              )}
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {data.extremities.inspection && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.extremities.inspection',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Inspection',
                      }
                    )}
                    value={data.extremities.inspection}
                  />
                )}
                {data.extremities.edema && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.extremities.edema',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Edema',
                      }
                    )}
                    value={data.extremities.edema}
                  />
                )}
                {data.extremities.skinHair && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.extremities.skinHair',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Skin/Hair',
                      }
                    )}
                    value={data.extremities.skinHair}
                  />
                )}
                {data.extremities.pulses && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.extremities.pulses',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Pulses',
                      }
                    )}
                    value={data.extremities.pulses}
                  />
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* Neurological Section */}
        {hasNeurologicalContent && (
          <Accordion.Item value="neurological">
            <Accordion.Control>
              {renderSectionTitle(
                t('examination.physicalExamination.neurological.title', {
                  ns: 'patient-pages',
                  defaultValue: 'Neurological',
                }),
                neurologicalIsNormal,
                hasNeurologicalContent
              )}
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {data.neurological.alertnessOrientation && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.neurological.alertnessOrientation',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Alertness/Orientation',
                      }
                    )}
                    value={data.neurological.alertnessOrientation}
                  />
                )}
                {data.neurological.speech && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.neurological.speech',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Speech',
                      }
                    )}
                    value={data.neurological.speech}
                  />
                )}
                {data.neurological.cranialNerves && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.neurological.cranialNerves',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Cranial Nerves',
                      }
                    )}
                    value={data.neurological.cranialNerves}
                  />
                )}
                {data.neurological.muscleStrength && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.neurological.muscleStrength',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Muscle Strength',
                      }
                    )}
                    value={data.neurological.muscleStrength}
                  />
                )}
                {data.neurological.sensation && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.neurological.sensation',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Sensation',
                      }
                    )}
                    value={data.neurological.sensation}
                  />
                )}
                {data.neurological.gaitBalance && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.neurological.gaitBalance',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Gait/Balance',
                      }
                    )}
                    value={data.neurological.gaitBalance}
                  />
                )}
                {data.neurological.cerebellarSigns && (
                  <LabeledField
                    label={t(
                      'examination.physicalExamination.neurological.cerebellarSigns',
                      {
                        ns: 'patient-pages',
                        defaultValue: 'Cerebellar Signs',
                      }
                    )}
                    value={data.neurological.cerebellarSigns}
                  />
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}
      </Accordion>

      {/* WNL-only sections (displayed as simple rows outside accordion) */}
      {(headNeckWnlOnly ||
        thoraxWnlOnly ||
        abdomenWnlOnly ||
        extremitiesWnlOnly ||
        neurologicalWnlOnly) && (
        <Stack gap="xs">
          {headNeckWnlOnly &&
            renderWnlOnlySection(
              t('examination.physicalExamination.headNeck.title', {
                ns: 'patient-pages',
                defaultValue: 'Head & Neck',
              })
            )}
          {thoraxWnlOnly &&
            renderWnlOnlySection(
              t('examination.physicalExamination.thorax.title', {
                ns: 'patient-pages',
                defaultValue: 'Thorax',
              })
            )}
          {abdomenWnlOnly &&
            renderWnlOnlySection(
              t('examination.physicalExamination.abdomen.title', {
                ns: 'patient-pages',
                defaultValue: 'Abdomen',
              })
            )}
          {extremitiesWnlOnly &&
            renderWnlOnlySection(
              t('examination.physicalExamination.extremities.title', {
                ns: 'patient-pages',
                defaultValue: 'Extremities',
              })
            )}
          {neurologicalWnlOnly &&
            renderWnlOnlySection(
              t('examination.physicalExamination.neurological.title', {
                ns: 'patient-pages',
                defaultValue: 'Neurological',
              })
            )}
        </Stack>
      )}
      </Stack>
    </Paper>
  );
};
